/**
 * Step 4 State A — WhatsApp Connect (Embedded Signup).
 *
 * 2026-05-22 redesign:
 *   - 14px rounded logo well + text-xl heading + text-base leading-relaxed
 *   - p-7 sm:p-9 padding
 *   - Inline 'I'll set up WhatsApp later' checkbox next to Connect button
 *     (toggles parent page's local willSkip state; commits on Done click)
 *
 * Existing logic preserved:
 *   - Loads /oauth/whatsapp/config (app_id, config_id, etc.)
 *   - Loads Meta JS SDK
 *   - FB.login → captures auth code + WA session_info → POSTs to
 *     /oauth/whatsapp/exchange (which writes waba_paired_at, etc.)
 *   - TP-conflict detection (third-party partner already connected)
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import { apiRequest } from '@/lib/api';
import { platformLogo } from '@/lib/attributionDisplay';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface WhatsAppConfig {
  app_id: string;
  config_id: string;
  graph_version: string;
  es_version: string;
  session_info_version: string;
  feature_type: string;
}

interface SessionInfo {
  waba_id: string;
  phone_number_id: string;
  business_id?: string;
}

interface WAEmbeddedSignupMessage {
  type: 'WA_EMBEDDED_SIGNUP';
  // Meta sends several success variants: 'FINISH' (Cloud API new number),
  // 'FINISH_ONLY_WABA', and 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
  // (Coexistence). All start with FINISH. Plus 'CANCEL' / 'ERROR'.
  event: string;
  data?: {
    waba_id?: string;
    phone_number_id?: string;
    business_id?: string;
    error_message?: string;
  };
}

interface FBLoginResponse {
  authResponse?: { code?: string } | null;
}

interface FBSDK {
  init: (options: Record<string, unknown>) => void;
  login: (
    callback: (response: FBLoginResponse) => void,
    options: Record<string, unknown>,
  ) => void;
}

interface Props {
  /** Local "I'll skip" checkbox state controlled by the parent page. */
  willSkip: boolean;
  onWillSkipChange: (next: boolean) => void;
}

export function WhatsAppConnectCard({ willSkip, onWillSkipChange }: Props) {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<WhatsAppConfig | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [busy, setBusy] = useState(false);
  // True from "popup closed with code" through "exchange in flight". Replaces
  // the connect-UI render so the user doesn't briefly see the explainer card
  // again between popup-close and the parent swapping us out for
  // WhatsAppConnectedCard (after go-live-status repolls). Persists across
  // exchange success since the parent re-renders and unmounts us anyway.
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tpConflict, setTpConflict] = useState(false);

  const sessionInfoRef = useRef<SessionInfo | null>(null);
  const codeRef = useRef<string | null>(null);

  // Test toggle: `?wa=api` launches the Default (Cloud API attach) flow instead
  // of Coexistence — omits featureType and persists coexistence=false. No picker
  // UI yet (deferred until we validate the WhatsApp API path with a customer).
  const isApiPath =
    new URLSearchParams(window.location.search).get('wa') === 'api';

  // 1. Load config + Meta JS SDK on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiRequest<WhatsAppConfig>('/oauth/whatsapp/config');
        if (cancelled) return;
        setConfig(cfg);
        await loadFbSdk(cfg.app_id, cfg.graph_version);
        if (!cancelled) setSdkReady(true);
      } catch {
        if (!cancelled) {
          setError("Could not load WhatsApp connection. Refresh and try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Listen for session_info from the ES popup.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') {
        return;
      }
      let raw: unknown;
      try {
        raw = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (
        !raw ||
        typeof raw !== 'object' ||
        (raw as { type?: unknown }).type !== 'WA_EMBEDDED_SIGNUP'
      ) {
        return;
      }
      const data = raw as WAEmbeddedSignupMessage;

      if (data.event.startsWith('FINISH')) {
        const info = data.data || {};
        if (!info.waba_id || !info.phone_number_id) {
          console.error('[WhatsAppConnectCard] FINISH missing required fields:', info);
          setBusy(false);
          setError("WhatsApp returned an incomplete response. Please try again.");
          return;
        }
        sessionInfoRef.current = {
          waba_id: info.waba_id,
          phone_number_id: info.phone_number_id,
          business_id: info.business_id,
        };
        void maybeExchange();
      } else if (data.event === 'CANCEL') {
        setBusy(false);
      } else if (data.event === 'ERROR') {
        const msg: string = data.data?.error_message || '';
        setBusy(false);
        if (/maximum number of partners|third-party access/i.test(msg)) {
          setTpConflict(true);
        } else {
          setError(msg || 'WhatsApp connection failed. Try again.');
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function maybeExchange() {
    const code = codeRef.current;
    const info = sessionInfoRef.current;
    if (!code || !info) return;

    setBusy(true);
    setActivating(true);
    setError(null);
    setTpConflict(false);
    try {
      await apiRequest('/oauth/whatsapp/exchange', {
        method: 'POST',
        body: {
          code,
          waba_id: info.waba_id,
          phone_number_id: info.phone_number_id,
          business_id: info.business_id,
          coexistence: !isApiPath,
        },
      });
      codeRef.current = null;
      sessionInfoRef.current = null;
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      await queryClient.invalidateQueries({ queryKey: ['onboarding', 'go-live-status'] });
      // Don't clear `activating` on success — parent swaps us out for
      // WhatsAppConnectedCard which shows its own activating UI for the
      // subscribe call. Seamless transition without a skeleton flash.
    } catch {
      setBusy(false);
      setActivating(false);
      setError("Couldn't complete the connection. Please try again.");
    }
  }

  function startConnect() {
    if (!config || !sdkReady) return;
    const FB = window.FB;
    if (!FB || !FB.login) {
      setError("WhatsApp connection isn't ready yet. Refresh and try again.");
      return;
    }
    setBusy(true);
    setError(null);
    setTpConflict(false);
    sessionInfoRef.current = null;
    codeRef.current = null;

    FB.login(
      (response: FBLoginResponse) => {
        const code = response?.authResponse?.code;
        if (code) {
          codeRef.current = code;
          void maybeExchange();
        } else {
          setBusy(false);
        }
      },
      {
        config_id: config.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          // Coexistence sets featureType; WhatsApp API (Default flow) omits it.
          ...(isApiPath ? {} : { featureType: config.feature_type }),
          sessionInfoVersion: config.session_info_version,
          version: config.es_version,
        },
      },
    );
  }

  // Activating state — popup closed with code, exchange in flight.
  // Mirrors WhatsAppConnectedCard's pending sub-state visually so the
  // transition WhatsAppConnectCard → WhatsAppConnectedCard reads as one
  // continuous "activating" experience.
  if (activating) {
    return (
      <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
        <h3 className="text-xl font-bold text-dark">Activating connection…</h3>
        <p className="mt-2 text-base text-muted-foreground leading-relaxed">
          Hooking HeyMotek up to your WhatsApp account. Usually under 30 seconds.
        </p>
      </div>
    );
  }

  if (tpConflict) {
    return (
      <div className="rounded-3xl border border-destructive/40 bg-destructive/10 p-7 sm:p-9">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="flex-1">
            <h3 className="text-xl font-bold text-destructive">Another tech partner is connected</h3>
            <p className="mt-2 text-base leading-relaxed">
              We see another tech partner is already connected to your WhatsApp Business Account.
              Please contact us at{' '}
              <a href="mailto:support@heymotek.com" className="underline">support@heymotek.com</a>{' '}
              — we'll sort it out together.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
      <div className="flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-green-50">
          <img src={platformLogo('whatsapp')} alt="" className="h-7 w-7" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-dark">Connect WhatsApp</h3>
          <p className="mt-2 text-base text-muted-foreground leading-relaxed">
            You'll authorize HeyMotek to attribute contacts on your WhatsApp number. Nothing changes about how you message. We work alongside your WhatsApp Business app.
          </p>

          <div className="mt-5 rounded-xl bg-bg-tint p-4 space-y-2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              We only collect contacts that come from your ads and website. Your day-to-day conversations stay private.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong className="text-dark">We never read or save your message content.</strong>
            </p>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            When the WhatsApp popup asks about syncing your chat history, choose <strong>Don't share</strong>.
          </p>

          {error && (
            <div className="mt-4">
              <Banner variant="error" size="compact">{error}</Banner>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              onClick={startConnect}
              disabled={!sdkReady || busy}
              className="bg-dark text-white hover:bg-dark/90 rounded-full px-6 py-3 font-bold"
            >
              {busy ? 'Opening WhatsApp…' : 'Connect WhatsApp'}
            </Button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Checkbox
                checked={willSkip}
                onCheckedChange={(c) => onWillSkipChange(c === true)}
              />
              <span>I'll set up WhatsApp later</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- FB SDK loader (module-scoped — loads once across all instances) -----

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB?: FBSDK;
  }
}

let sdkLoadPromise: Promise<void> | null = null;

function fbInitOptions(appId: string, graphVersion: string) {
  return {
    appId,
    cookie: true,
    autoLogAppEvents: true,
    xfbml: true,
    version: graphVersion,
  };
}

function loadFbSdk(appId: string, graphVersion: string): Promise<void> {
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    if (window.FB && typeof window.FB.init === 'function') {
      window.FB.init(fbInitOptions(appId, graphVersion));
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.onload = () => {
      if (!window.FB || typeof window.FB.init !== 'function') {
        reject(new Error('Meta SDK loaded but window.FB is unavailable'));
        return;
      }
      try {
        window.FB.init(fbInitOptions(appId, graphVersion));
        resolve();
      } catch (e) {
        reject(e);
      }
    };
    script.onerror = () => reject(new Error('Failed to load Meta JS SDK'));
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}
